import { cloneDeep, isEmpty, isEqual } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { getDateGroupNumber, getValidDateGroup, IAnalytics } from '~/src/dash/api/console-api';
import {
  IIASFirstAndThirdPartyRequestBody,
  queryFirstAndThirdPartyIasData,
} from '~/src/dash/api/ias-wrapper-api/ias-wrapper-api';
import { IFocusedInput } from '~/src/dash/components/calendar/calendar-wrapper';
import { DrillHorizontalBarChart } from '~/src/dash/components/charts/DrillHorizontalBarChart';
import { DashboardBreadCrumbs, IDashboardCrumb } from '~/src/dash/components/DashboardBreadCrumbs';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import {
  IObjectWithDefault,
  ITableCol,
  ITableGroupByForReportsDashboard,
  ITableGroupBysForReportsDashboard,
  ITableMetric,
  ITableMetrics,
  ORDER_DIRECTION,
  ValueType,
  valueTypes,
} from '~/src/dash/components/tables/common';
import { DrillTable } from '~/src/dash/components/tables/DrillTable';
import { genTimezoneList } from '~/src/dash/globals/timezone';
import { Panel, PanelBreak, Panels, Row, Rows, spacing } from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import {
  findCompareEndDate,
  findCompareStartDate,
  generateCalendarProps,
  generateCompareCalendarProps,
} from '~/src/dash/utils/calendar';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatDateEndDay, formatDateStartDay } from '~/src/dash/utils/date';
import { DateGroupDays } from '~/src/dash/api/console-api';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { obfuscateDataLabel } from '~/src/dash/utils/obfuscate';
import { routeName } from '~/src/dash/utils/routes';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { dashboardMainChartColorBank } from '~/src/dash/views/common/dashboards-consts';
import { combineDeviceTypeFilters } from '~/src/dash/views/common/ias-data-consts';
import { IIASDashboardFilter } from '~/src/dash/views/ias-publisher/IASDashboard/common/types';
import {
  formatComparisonKeys,
  reportsDashboardConfig,
} from '~/src/dash/views/ias-publisher/IASReportsDashboard/config';
import { ReportsDashboardContext } from '~/src/dash/views/ias-publisher/IASReportsDashboard/contexts';
import {
  IChartDataItem,
  IDrillItem,
  IReportsDashboardRow,
  ReportsDashboardGroupByKey,
  ReportsDashboardMetricKey,
} from '~/src/dash/views/ias-publisher/IASReportsDashboard/ReportsDashboard/common/types';
import { ReportsDashboardFilters } from '~/src/dash/views/ias-publisher/IASReportsDashboard/ReportsDashboard/ReportsDashboardFilters';
import { ISingleSelectOption } from '~/src/dash/views/singleselect-view';
import {
  generateSnackbarState,
  ISnackbarState,
  SnackbarView,
} from '~/src/dash/views/snackbar-view';
import { DemandSources } from '~/src/dash/globals/demand-sources';

// get config
const cfg = reportsDashboardConfig;

const isFiltersSelected = (filters: Record<string, IIASDashboardFilter>) => {
  if (isEmpty(filters)) {
    return true;
  }
  return Object.values(filters).some((filter) =>
    Object.values(filter.list).some((item) => item.selected),
  );
};

const extractAllAPIKeysFromGroupBysAndMetrics = (
  groupBys: Partial<ITableGroupBysForReportsDashboard>,
  metrics: ITableMetrics,
) => {
  const keys = {} as Record<string, boolean>;
  Object.values(groupBys).forEach((groupBy) => {
    keys[groupBy.apiKey] = true;
    keys[groupBy.displayKey] = true;
  });
  Object.values(metrics).forEach((metric) => {
    keys[metric.apiKey] = true;
  });
  return Object.keys(keys);
};

interface IFilterDataParams {
  additionalKeys?: string[];
  data: IAnalytics[];
  groupByAPIKey: string;
  isComparisonData?: boolean;
  keys: string[];
  currentThreshold: string | number;
}

const filterData = ({
  additionalKeys = [],
  data,
  groupByAPIKey,
  keys,
  currentThreshold,
}: IFilterDataParams) => {
  const updatedKeys = [...keys, ...additionalKeys];
  const orderedDataKeys: string[] = [];
  const filteredData: Record<string, IAnalytics> = {};
  data.forEach((item) => {
    const row = {} as IAnalytics;
    updatedKeys.forEach((key) => {
      if (key !== 'BidderTypeName') {
        row[key] = item[key];
      }
    });
    // this fixes an edge case scenario due to a hardcoded transform on the backend
    const key = item[groupByAPIKey] === 'VGI CTV' ? DemandSources.nexstar : item[groupByAPIKey];
    if (currentThreshold !== 'all') {
      if (row.TotalImps > currentThreshold) {
        filteredData[key as string] = row;
        orderedDataKeys.push(key as string);
      }
    } else {
      filteredData[key as string] = row;
      orderedDataKeys.push(key as string);
    }
  });
  return { data: filteredData, orderedDataKeys };
};

interface IFormatMainData {
  [key: string]: Record<string, IAnalytics>;
}

interface IFormatDataParams {
  primaryData: IPrimaryData;
  comparisonData?: Record<string, IAnalytics>;
  isComparedToEnabled?: boolean;
}

const formatData = ({
  primaryData,
  comparisonData = {},
  isComparedToEnabled = false,
}: IFormatDataParams) => {
  const formattedData: IFormatMainData = {};
  const { data, orderedDataKeys } = primaryData;
  orderedDataKeys.forEach((key) => {
    if (isComparedToEnabled) {
      formattedData[key] = {
        main: data[key],
        compare: comparisonData[key],
      };
    } else {
      formattedData[key] = { main: data[key] };
    }
  });
  return {
    data: formattedData,
    orderedDataKeys: primaryData.orderedDataKeys,
  };
};

const getSelectedListItem = (list: IMenuList) => {
  return Object.values(list).find(({ selected }) => selected === true);
};

const generateGroupBySelectItemsFromConfig = (
  groupBys: Partial<ITableGroupBysForReportsDashboard>,
): ISingleSelectOption[] => {
  const items = [] as ISingleSelectOption[];
  Object.values(groupBys).forEach((groupBy) => {
    items.push({
      label: groupBy.pluralLabel,
      value: groupBy.label,
      subheader: groupBy.subheader,
    });
  });
  return items;
};

const generateOrderBySelectItemsFromConfig = (orderBys: ITableMetrics): ISingleSelectOption[] => {
  const items = [] as ISingleSelectOption[];
  Object.values(orderBys).forEach((orderBy) => {
    if (!orderBy.isCompareValue) {
      items.push({
        label: orderBy.label,
        value: orderBy.label,
        subheader: orderBy.subheader,
      });
    }
  });
  return items;
};

const generateThresholdSelectItemsFromConfig = (
  thresholds: Record<string, string | number>,
): ISingleSelectOption[] => {
  const items = [] as ISingleSelectOption[];
  Object.entries(thresholds).forEach((threshold) => {
    items.push({
      label: threshold[0],
      value: String(threshold[1]),
    });
  });
  return items;
};

const extractDefaultFromArrayOfObjects = (array: IObjectWithDefault[]): IObjectWithDefault => {
  return array.find(({ isDefault }) => isDefault) || array[0];
};

const genOrderByList = (array: ITableMetric[]) => {
  const selected = array.find(({ isDefault }) => isDefault) || array[0];
  const orderByList: IMenuList = {};
  array.forEach((arr) => {
    orderByList[arr.label] = {
      title: arr.label,
      selected: false,
      ID: arr.label,
      subheader: arr.subheader,
    };
  });
  orderByList[selected.label].selected = true;
  return orderByList;
};

const generateCols = (
  groupBy: ITableGroupByForReportsDashboard | undefined,
  metrics: ITableMetric[],
  isComparedToEnabled: boolean,
  currentOrderByList: IMenuList,
): ITableCol[] => {
  if (!groupBy) {
    return [];
  }
  const cols = [
    {
      label: groupBy.label,
      alias: groupBy.alias || '',
      apiKey: groupBy.apiKey,
      displayKey: groupBy.displayKey,
      requestKey: groupBy.requestKey,
      defaultOrderDirection: ORDER_DIRECTION.ASC,
      isSelectable: true,
      isTableControllingSort: !!groupBy.isTableControllingSort,
      valueType: valueTypes.NAME,
      tooltipMessage: groupBy.tooltipMessage || '',
      headerStyle: groupBy.headerStyle || {},
    } as ITableCol,
  ];
  metrics.forEach((metric) => {
    if (currentOrderByList[metric.label].selected) {
      if ((metric.isCompareValue && isComparedToEnabled) || !metric.isCompareValue) {
        cols.push({
          label: metric.label,
          alias: metric.alias || '',
          apiKey: metric.apiKey,
          displayKey: metric.apiKey,
          requestKey: metric.requestKey,
          defaultOrderDirection: metric.defaultOrderDirection,
          isSelectable: metric.isSelectable,
          isTableControllingSort: !!metric.isTableControllingSort,
          isCompareValue: !!metric.isCompareValue,
          valueType: metric.valueType,
          tooltipMessage: metric.tooltipMessage || '',
          headerStyle: metric.headerStyle || {},
        });
      }
    }
  });
  return cols;
};

const calculateCompareValue = (
  mainValue: number,
  comparisonValue: number,
  valueType: ValueType,
): number | undefined => {
  const { PERCENT_CHANGE } = valueTypes;
  switch (valueType) {
    case PERCENT_CHANGE:
      return ((mainValue - comparisonValue) / comparisonValue) * 100;
    default:
      return;
  }
};

const generateRows = (
  cols: ITableCol[],
  mainData: IMainData,
  isComparedToEnabled: boolean,
  groupByLabel: string,
  username: string,
) => {
  const rows = [] as Array<Record<string, any>>;
  const { data, orderedDataKeys } = mainData;
  orderedDataKeys.forEach((key, idx) => {
    const mainItem = data[key].main;
    const comparisonItem = data[key].compare;
    const row = {} as any;
    cols.forEach((col, i) => {
      if (i === 0) {
        row.Name = obfuscateDataLabel(
          username,
          mainItem[col.displayKey] as string,
          groupByLabel,
          `${idx + 1}`,
        );
        row.ID = mainItem[col.apiKey];
      } else {
        if (isComparedToEnabled && col.isCompareValue) {
          if (comparisonItem) {
            const value = calculateCompareValue(
              mainItem[col.apiKey] as number,
              comparisonItem[col.apiKey] as number,
              col.valueType,
            );
            row[col.label] = value;
          } else {
            row[col.label] = '';
          }
        } else {
          row[col.label] = mainItem[col.apiKey];
        }
      }
    });
    rows.push(row);
  });
  return rows;
};

const limitRowCount = (totalRows: number, limit: number) => {
  return totalRows > limit ? limit : totalRows;
};

const generateChartData = (
  mainData: IMainData,
  groupBy: ITableGroupByForReportsDashboard,
  orderBy: ITableMetric,
  isComparedToEnabled: boolean,
  colorBank: string[],
  username: string,
) => {
  const chartData: Record<string, IChartDataItem> = {};
  const { data, orderedDataKeys } = mainData;
  let n = 0;
  orderedDataKeys.forEach((key, i) => {
    n++;
    const item = data[key];
    const ID = item.main[groupBy.apiKey] as string;
    const primaryValue = item.main[orderBy.apiKey] as number;
    const compareValue =
      isComparedToEnabled && !isEmpty(item.compare)
        ? {
            comparisonValue: (item.compare[orderBy.apiKey] as number) || undefined,
            percentChange:
              calculateCompareValue(
                primaryValue,
                item.compare[orderBy.apiKey] as number,
                valueTypes.PERCENT_CHANGE,
              ) || undefined,
          }
        : {};
    chartData[ID] = {
      id: ID,
      name: obfuscateDataLabel(
        username,
        item.main[groupBy.displayKey] as string,
        groupBy.label,
        `${n}`,
      ),
      valueType: orderBy.valueType,
      primaryValue,
      ...compareValue,
      color: colorBank[i % colorBank.length],
    };
  });
  return chartData;
};

const filterChartData = (orderedDataKeys: string[], chartData: Record<string, IChartDataItem>) => {
  const result: IChartDataItem[] = [];
  orderedDataKeys.forEach((key) => {
    result.push(chartData[key]);
  });
  return result;
};

const generateBreadCrumbItems = (drillItems: IDrillItem[]) => {
  const items: IDashboardCrumb[] = [];
  drillItems.forEach((item) => {
    items.push({
      sourceLabel: item.groupByKey,
      itemLabel: item.name,
    });
  });
  return items;
};

interface IMainData {
  data: { [key: string]: Record<string, IAnalytics> };
  orderedDataKeys: string[];
}

interface IPrimaryData {
  data: Record<string, IAnalytics>;
  orderedDataKeys: string[];
}

// set needed config consts
const dataKeys = extractAllAPIKeysFromGroupBysAndMetrics(cfg.groupBys, cfg.mainMetrics);
const allMetricKeys = Object.keys(cfg.mainMetrics);

// set needed consts
// booleans created for readability of queryData
const isComparisonQuery = true;
const isDrillQuery = true;

export const ReportsDashboard = () => {
  // custom hook consts
  const { classes, location, session, user } = useBaseStore();
  // states and refs
  const isMounted = useRef(false);
  // loading
  const [dataLoadState, setDataLoadState] = useState(LoadingStateLoading);
  // data
  const [primaryData, setPrimaryData] = useState({} as IPrimaryData);
  const [comparisonKeys, setComparisonKeys] = useState<string[]>([]);
  const [comparisonData, setComparisonData] = useState<Record<string, IAnalytics>>({});
  const [mainData, setMainData] = useState({} as IMainData);
  // track handlers
  const [handleType, setHandleType] = useState('');
  // timezones
  const [timezone, setTimezone] = useState(session.publisher.Timezone);
  const [timezonesList, setTimezonesList] = useState<IMenuList>(
    genTimezoneList(session.publisher.Timezone),
  );
  // calendars
  const [rangeSelectedStr, setRangeSelectedStr] = useState('Last 7 Days');
  const [calendar, setCalendar] = useState(generateCalendarProps(session.publisher.Timezone));
  const [isNewCalReady, setIsNewCalReady] = useState(false);
  const [calFocusedInput, setCalFocusedInput] = useState<IFocusedInput>(null);
  const [compareCalendar, setCompareCalendar] = useState(
    generateCompareCalendarProps(session.publisher.Timezone),
  );
  const [compFocusedInput, setCompFocusedInput] = useState<IFocusedInput>(null);
  const [densityRangeAgg, setDensityRangeAgg] = useState(DateGroupDays);
  const [isComparedToEnabled, setIsComparedToEnabled] = useState(true);
  // this ensures comparison calendar is up to date before api call
  const [isCompCalUpdated, setIsCompCalUpdated] = useState(false);
  // filters
  const [filters, setFilters] = useState<Record<string, IIASDashboardFilter>>(cfg.filters);
  // group by
  const [currentGroupBy, setCurrentGroupBy] = useState(
    extractDefaultFromArrayOfObjects(
      Object.values(cfg.groupBys),
    ) as ITableGroupByForReportsDashboard,
  );
  // order by
  const [currentOrderBy, setCurrentOrderBy] = useState(
    extractDefaultFromArrayOfObjects(Object.values(cfg.mainMetrics)) as ITableMetric,
  );
  const [currentOrderByList, setCurrentOrderByList] = useState<IMenuList>(
    genOrderByList(Object.values(cfg.mainMetrics)),
  );
  // threshold
  const [currentThreshold, setCurrentThreshold] = useState(Object.values(cfg.threshold)[0]);
  // table loading
  const [areRowsUpdating, setAreRowsUpdating] = useState(true);
  // table specific
  const [displayedTableRowIDs, setDisplayedTableRowIDs] = useState<string[]>([]);
  // drills
  const [isDrillActive, setIsDrillActive] = useState(false);
  const [drillGroupBy, setDrillGroupBy] = useState(currentGroupBy);
  const [drillItems, setDrillItems] = useState([] as IDrillItem[]);
  // cols and rows
  const [cols, setCols] = useState<ITableCol[]>(
    generateCols(
      currentGroupBy,
      Object.values(cfg.mainMetrics),
      isComparedToEnabled,
      currentOrderByList,
    ),
  );
  const [rows, setRows] = useState<Array<Record<string, any>>>([]);
  const [metricKeys, setMetricKeys] = useState<string[]>(['Total Tracked Ads']);
  // main bar chart
  const [isChartUpdating, setIsChartUpdating] = useState(true);
  const [chartData, setChartData] = useState<Record<string, IChartDataItem>>({});
  const [displayedChartData, setDisplayedChartData] = useState<IChartDataItem[]>([]);
  // snackbar state
  const [snackbar, setSnackbar] = useState<ISnackbarState>(generateSnackbarState());
  // other consts
  const publisherID = session.publisher.ID;
  const { CurrencyType } = session.publisher.ExchangeRate;
  const { pathname } = location;

  const { data } = primaryData;

  // useEffects
  // make the initial api calls on load
  useEffect(() => {
    queryData({});
  }, []);

  // handles timezone changes
  useEffect(() => {
    if (isMounted.current) {
      const selectedTimezone = getSelectedListItem(timezonesList);
      setTimezone(selectedTimezone ? selectedTimezone.ID : session.publisher.Timezone);
    }
  }, [timezonesList]);

  // this resets the calendar on timezone change
  useEffect(() => {
    if (isMounted.current) {
      unstable_batchedUpdates(() => {
        setCalendar(generateCalendarProps(timezone));
        setCompareCalendar(generateCompareCalendarProps(timezone));
      });
    }
  }, [timezone]);

  // handles calendar changes
  useEffect(() => {
    if (
      isMounted.current &&
      handleType !== 'handleDensityRangeChangeAgg' &&
      handleType !== 'handleCalendarFocusedInputChange'
    ) {
      const updatedCompStartDate = findCompareStartDate({
        timezone,
        startDate: calendar.displayStartDate,
        endDate: calendar.endDate,
        rangeSelected: rangeSelectedStr,
      });
      const updatedCompEndDate = findCompareEndDate({
        timezone,
        startDate: calendar.displayStartDate,
        endDate: calendar.displayEndDate,
        rangeSelected: rangeSelectedStr,
      });
      const clonedCompareCalendar = cloneDeep(compareCalendar);
      clonedCompareCalendar.startDate = updatedCompStartDate;
      clonedCompareCalendar.endDate = updatedCompEndDate;
      clonedCompareCalendar.displayStartDate = updatedCompStartDate;
      clonedCompareCalendar.displayEndDate = updatedCompEndDate;
      unstable_batchedUpdates(() => {
        setCompareCalendar(clonedCompareCalendar);
        setIsCompCalUpdated(true);
      });
    }
  }, [timezone, calendar, densityRangeAgg]);

  // if filters change...
  useEffect(() => {
    if (isMounted.current) {
      if (drillItems.length > 0) {
        queryData({ isDrillQuery });
      } else {
        queryData({});
      }
    }
  }, [filters, timezone]);

  // if the calendar changes...
  useEffect(() => {
    if (
      (isMounted.current &&
        handleType !== 'handleCalendarDateChange' &&
        handleType !== 'handleCalendarFocusedInputChange' &&
        handleType !== 'handleChangeAggRangeSelected') ||
      (isCompCalUpdated && isNewCalReady)
    ) {
      if (drillItems.length > 0) {
        queryData({ isDrillQuery });
      } else {
        queryData({});
      }
      unstable_batchedUpdates(() => {
        setIsNewCalReady(false);
        setIsCompCalUpdated(false);
      });
    }
  }, [isCompCalUpdated, densityRangeAgg, isNewCalReady]);

  // if the group by or order by changes...
  useEffect(() => {
    if (isMounted.current) {
      resetDrill();
    }
  }, [currentOrderBy, currentGroupBy, currentThreshold]);

  // get the primary data keys
  useEffect(() => {
    if (isMounted.current) {
      unstable_batchedUpdates(() => {
        setComparisonKeys(primaryData.orderedDataKeys || []);
        if (!isComparedToEnabled) {
          setMainData(formatData({ primaryData }));
        }
      });
    }
  }, [primaryData]);

  // get comparison data
  useEffect(() => {
    if (isMounted.current && isComparedToEnabled) {
      if (comparisonKeys.length > 0) {
        queryData({ isComparisonQuery });
      } else {
        setComparisonData({});
      }
    }
  }, [comparisonKeys]);

  // format data for comparison
  useEffect(() => {
    if (isMounted.current) {
      setMainData(
        formatData({
          primaryData,
          comparisonData,
          isComparedToEnabled,
        }),
      );
    }
  }, [comparisonData]);

  // Notes: This prevents an unnecessary api call when disabling comparison data
  useEffect(() => {
    if (isMounted.current && handleType === 'handleCompareEnabled') {
      if (isComparedToEnabled) {
        resetDrill();
      } else {
        setComparisonData({});
      }
    }
  }, [isComparedToEnabled]);

  useEffect(() => {
    if (isMounted.current) {
      if (drillItems.length > 0) {
        queryData({ isDrillQuery });
      } else {
        queryData({});
      }
    }
  }, [drillItems]);

  useEffect(() => {
    if (isMounted.current) {
      setCols(
        isDrillActive
          ? generateCols(
              cfg.groupBys[drillGroupBy.label],
              Object.values(cfg.mainMetrics),
              isComparedToEnabled,
              currentOrderByList,
            )
          : generateCols(
              currentGroupBy,
              Object.values(cfg.mainMetrics),
              isComparedToEnabled,
              currentOrderByList,
            ),
      );
      setMetricKeys(allMetricKeys.filter((key) => currentOrderByList[key].selected));
    }
  }, [isDrillActive, currentGroupBy, drillGroupBy, isComparedToEnabled, currentOrderByList]);

  useEffect(() => {
    if (isMounted.current && cols.length > 0 && !isEmpty(mainData)) {
      unstable_batchedUpdates(() => {
        setRows(
          generateRows(cols, mainData, isComparedToEnabled, drillGroupBy.label, user.user.Username),
        );
        setChartData(
          generateChartData(
            mainData,
            drillGroupBy,
            currentOrderBy,
            isComparedToEnabled,
            Object.values(dashboardMainChartColorBank),
            user.user.Username,
          ),
        );
      });
    }
  }, [cols, mainData, isComparedToEnabled, drillGroupBy, currentOrderBy, isComparedToEnabled]);

  useEffect(() => {
    if (isMounted.current && !isEmpty(chartData) && displayedTableRowIDs.length > 0) {
      const filteredChartData = filterChartData(displayedTableRowIDs, chartData);
      setDisplayedChartData(filteredChartData);
    }
  }, [displayedTableRowIDs, chartData]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    }
  });

  // calendar functions
  const handleChangeAggRangeSelected = (
    rangeSelected: string,
    isCompareCalendar: boolean,
    applied?: boolean,
  ): void => {
    setHandleType('handleChangeAggRangeSelected');
    if (isCompareCalendar) {
      const clonedCompareCalendar = cloneDeep(compareCalendar);
      if (applied) {
        clonedCompareCalendar.rangeSelected = rangeSelected;
        clonedCompareCalendar.rangeDisplaySelected = rangeSelected;
      } else {
        clonedCompareCalendar.rangeDisplaySelected = rangeSelected;
      }
      unstable_batchedUpdates(() => {
        setCompareCalendar(clonedCompareCalendar);
      });
    } else {
      const clonedCalendar = cloneDeep(calendar);
      if (applied) {
        clonedCalendar.rangeSelected = rangeSelected;
        clonedCalendar.rangeDisplaySelected = rangeSelected;
      } else {
        clonedCalendar.rangeDisplaySelected = rangeSelected;
      }
      unstable_batchedUpdates(() => {
        setRangeSelectedStr(rangeSelected);
        setCalendar(clonedCalendar);
      });
    }
  };

  // this causes a console error due to a bad setState within the nested component
  // no fix needed for now
  const handleCalendarDateChange = (
    isCompareCalendar: boolean,
  ): ((start: moment.Moment, end: moment.Moment, isCustom: boolean) => void) => {
    setHandleType('handleCalendarDateChange');
    if (isCompareCalendar) {
      return (start: moment.Moment, end: moment.Moment, isCustom: boolean) => {
        if (isCustom) {
          handleChangeAggRangeSelected('Custom', true, true);
        }
        const clonedCompareCalendar = cloneDeep(compareCalendar);
        clonedCompareCalendar.displayStartDate = start;
        clonedCompareCalendar.displayEndDate = end;
        unstable_batchedUpdates(() => {
          setCompareCalendar(clonedCompareCalendar);
        });
      };
    } else {
      return (start: moment.Moment, end: moment.Moment, isCustom: boolean) => {
        if (isCustom) {
          handleChangeAggRangeSelected('Custom', false, true);
        }
        const clonedCalendar = cloneDeep(calendar);
        clonedCalendar.displayStartDate = start;
        clonedCalendar.displayEndDate = end;
        setCalendar(clonedCalendar);
      };
    }
  };

  const handleDateRangeChange = (
    start: moment.Moment,
    end: moment.Moment,
    isCompareCalendar: boolean,
  ): void => {
    setHandleType('handleDateRangeChange');
    const startDate = cloneDeep(start).startOf('day');
    const endDate = cloneDeep(end).endOf('day');
    if (isCompareCalendar) {
      const clonedCompareCalendar = cloneDeep(compareCalendar);
      clonedCompareCalendar.startDate = startDate;
      clonedCompareCalendar.endDate = endDate;
      unstable_batchedUpdates(() => {
        setCompareCalendar(clonedCompareCalendar);
        setDensityRangeAgg(getValidDateGroup(densityRangeAgg, start, end));
        setIsCompCalUpdated(true);
      });
    } else {
      const clonedCalendar = cloneDeep(calendar);
      clonedCalendar.startDate = startDate;
      clonedCalendar.endDate = endDate;
      unstable_batchedUpdates(() => {
        setCalendar(clonedCalendar);
        setDensityRangeAgg(getValidDateGroup(densityRangeAgg, start, end));
      });
    }
    setIsNewCalReady(true);
  };

  const handleCalendarFocusedInputChange = (
    isCompareCalendar: boolean,
  ): ((focusedInput: IFocusedInput) => void) => {
    setHandleType('handleCalendarFocusedInputChange');
    if (isCompareCalendar) {
      return (focusedInput: IFocusedInput) => {
        const clonedCalendar = cloneDeep(calendar);
        const clonedCompareCalendar = cloneDeep(compareCalendar);
        if (focusedInput === null) {
          unstable_batchedUpdates(() => {
            setCalFocusedInput(null);
            setCompFocusedInput(null);
          });
          clonedCalendar.displayStartDate = calendar.startDate;
          clonedCalendar.displayEndDate = calendar.endDate;
          clonedCompareCalendar.displayStartDate = compareCalendar.startDate;
          clonedCompareCalendar.displayEndDate = compareCalendar.endDate;
        } else {
          unstable_batchedUpdates(() => {
            setCalFocusedInput(null);
            setCompFocusedInput(focusedInput);
          });
        }
      };
    } else {
      return (focusedInput: IFocusedInput) => {
        const clonedCalendar = cloneDeep(calendar);
        const clonedCompareCalendar = cloneDeep(compareCalendar);
        if (focusedInput === null) {
          unstable_batchedUpdates(() => {
            setCalFocusedInput(null);
            setCompFocusedInput(null);
          });
          clonedCalendar.displayStartDate = calendar.startDate;
          clonedCalendar.displayEndDate = calendar.endDate;
          clonedCompareCalendar.displayStartDate = compareCalendar.startDate;
          clonedCompareCalendar.displayEndDate = compareCalendar.endDate;
        } else {
          unstable_batchedUpdates(() => {
            setCalFocusedInput(focusedInput);
            setCompFocusedInput(null);
          });
        }
      };
    }
  };

  const handleCompareEnabled = () => {
    setHandleType('handleCompareEnabled');
    setIsComparedToEnabled(!isComparedToEnabled);
  };

  // timezone functions
  const handleTimezoneOnChange = (list: IMenuList) => {
    setHandleType('handleTimezoneOnChange');
    setTimezonesList(list);
  };

  // filter pill functions
  const handleFilterOnChange = (menuTitle: string) => (values: IMenuList) => {
    setHandleType('handleFilterOnChange');
    const clonedFilters = cloneDeep(filters);
    clonedFilters[menuTitle].list = values;
    setFilters(clonedFilters);
  };

  const handleFilterAccept = () => {
    setHandleType('handleFilterAccept');
    setFilters(filters);
  };

  const handleClearFilters = () => {
    setHandleType('handleClearFilters');
    const clonedFilters = cloneDeep(filters);
    Object.values(clonedFilters).forEach((filter) => {
      Object.values(filter.list).forEach((item) => {
        item.selected = false;
      });
    });
    unstable_batchedUpdates(() => {
      setFilters(clonedFilters);
    });
  };

  const handleGroupByOnChange = (_label: string, value: ReportsDashboardGroupByKey) => {
    const groupBy = cfg.groupBys[value];
    if (groupBy) {
      setHandleType('handleGroupByOnChange');
      unstable_batchedUpdates(() => {
        setCurrentGroupBy(groupBy);
        setDrillGroupBy(groupBy);
      });
      setHandleType('handleGroupByOnChange');
    }
  };

  const handleOrderByOnChange = (_label: string, value: ReportsDashboardMetricKey) => {
    setHandleType('handleOrderByOnChange');
    setCurrentOrderBy(cfg.mainMetrics[value]);
  };

  const handleOrderByListOnChange = (value: IMenuList) => {
    setHandleType('handleOrderByListOnChange');
    setCurrentOrderByList(value);
  };

  const handleThresholdOnChange = (_label: string, value: string) => {
    setHandleType('handleThresholdOnChange');
    setCurrentThreshold(value);
  };

  const handleRowClick = (row: IReportsDashboardRow | IChartDataItem) => {
    setHandleType('handleRowClick');
    // drilling is currently unsupported for white label.
    // TODO: remove drillItems.length check once drill paths are figured out.
    if (!isEmpty(row) && drillItems.length > 0) {
      const nodeLevel = drillItems.length;
      const nextKey = cfg.groupBys[currentGroupBy.drillPath[nodeLevel]]?.label;
      const requestKey = nextKey ? cfg.groupBys[nextKey]?.requestKey : '';
      const clonedDrillItems = cloneDeep(drillItems);
      // we ignore type id/ID/name/Name because only one or the other set will exist
      // mui component needs uppercase; recharts component needs lowercase
      // @ts-ignore
      const id = row.ID || row.id;
      // @ts-ignore
      const name = row.Name || row.name;
      clonedDrillItems.push({
        id: id as string,
        name: name as string,
        apiKey: drillGroupBy.apiKey,
        matchKey: drillGroupBy.matchKey,
        requestKey: requestKey || '',
        groupByKey: drillGroupBy.label,
        nextGroupByKey: nextKey,
        drillFilter: {
          [drillGroupBy.matchKey]: formatComparisonKeys([id as string], drillGroupBy.apiKey),
        },
      });
      unstable_batchedUpdates(() => {
        setIsDrillActive(true);
        setDrillItems(clonedDrillItems);
      });
    }
  };

  const handleTableDisplayChange = (rowIDs: string[]) => {
    // hard cap comparison for now
    const limitedRowIDs = rowIDs.length > 100 ? rowIDs.slice(0, 100) : rowIDs;
    if (!isEqual(limitedRowIDs, displayedTableRowIDs)) {
      setDisplayedTableRowIDs(rowIDs);
    }
  };

  const handleBreadCrumbClick = (index: number) => {
    const groupBy = cfg.groupBys[drillItems[index]?.groupByKey];
    if (groupBy) {
      setHandleType('handleBreadCrumbClick');
      const clonedDrillItems = cloneDeep(drillItems);
      unstable_batchedUpdates(() => {
        setDrillItems(clonedDrillItems.slice(0, index));
        setDrillGroupBy(groupBy);
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({
      message: '',
      error: false,
      warning: false,
    });
  };

  const resetDrill = () => {
    unstable_batchedUpdates(() => {
      setDrillItems([]);
      setIsDrillActive(false);
      setDrillGroupBy(currentGroupBy);
    });
  };

  interface IQueryDataParams {
    isComparisonQuery?: boolean;
    isDrillQuery?: boolean;
  }

  // query for primary data
  const queryData = ({ isComparisonQuery = false, isDrillQuery = false }: IQueryDataParams) => {
    // reset load states
    if (!isComparisonQuery) {
      unstable_batchedUpdates(() => {
        setDataLoadState(LoadingStateLoading);
        setAreRowsUpdating(true);
        setIsChartUpdating(true);
      });
    }
    // query calendar
    const startDate = !isComparisonQuery
      ? formatDateStartDay(calendar.startDate)
      : formatDateStartDay(compareCalendar.startDate);
    const endDate = !isComparisonQuery
      ? formatDateEndDay(calendar.endDate)
      : formatDateEndDay(compareCalendar.endDate);

    // more params
    const grouping = densityRangeAgg;

    const deviceTypeFilters = {
      Formats: filters.Formats,
      'Device Types': filters['Device Types'],
    };

    const filterParams = Object.keys(filters).includes('Formats')
      ? combineDeviceTypeFilters(deviceTypeFilters)
      : [];
    const currentDrillItem = drillItems[drillItems.length - 1];

    const body = {
      GraphID: cfg.mainData.graph,
      StartDate: startDate,
      EndDate: endDate,
      Timezone: timezone,
      GroupingNumber: getDateGroupNumber(grouping),
      DeviceTypes: filterParams,
      TeamID: '15661',
      AnIDs: [session.publisher.IASAnID],
      Filters: {},
      NameSearch: '',
      IDSearch: '',
      ReportOrderByKey: 'TotalImps',
      ReportOrderBy: 'desc',
      GroupByKey: currentGroupBy.matchKey,
      Page: 0,
      PageSize: 10,
    } as IIASFirstAndThirdPartyRequestBody;
    queryFirstAndThirdPartyIasData(body, { publisher_id: publisherID })
      .then((data: any) => {
        let queryFailed = false;
        if (data[0].rows === null || data[0].rows.length === 0) {
          queryFailed = true;
          unstable_batchedUpdates(() => {
            setDataLoadState(LoadingStateNoData);
            setAreRowsUpdating(false);
            setIsChartUpdating(false);
            if (isComparisonQuery) {
              setComparisonData({});
            } else {
              setPrimaryData({
                data: {},
                orderedDataKeys: [],
              });
            }
          });
        }
        if (queryFailed && !isComparisonQuery) {
          return;
        }

        const groupByAPIKey =
          currentDrillItem && currentDrillItem?.nextGroupByKey
            ? cfg.groupBys[currentDrillItem.nextGroupByKey]?.apiKey
            : isDrillActive
            ? drillGroupBy.apiKey
            : currentGroupBy.apiKey;

        const filteredData = filterData({
          additionalKeys: !isComparisonQuery ? ['TotalRows'] : [],
          data: data[0].rows,
          groupByAPIKey: groupByAPIKey || '',
          keys: dataKeys,
          currentThreshold,
        });

        if (isEmpty(filteredData.data)) {
          unstable_batchedUpdates(() => {
            setDataLoadState(LoadingStateNoData);
            setPrimaryData(filteredData);
            setAreRowsUpdating(false);
            setIsChartUpdating(false);
          });
          return;
        }

        const groupBy =
          currentDrillItem && currentDrillItem?.nextGroupByKey
            ? cfg.groupBys[currentDrillItem.nextGroupByKey]
            : null;

        unstable_batchedUpdates(() => {
          setDataLoadState(LoadingStateHasData);
          if (isDrillQuery && currentDrillItem && currentDrillItem?.nextGroupByKey && groupBy) {
            setDrillGroupBy(groupBy);
          }
          if (!isComparisonQuery) {
            setPrimaryData(filteredData);
          } else {
            setComparisonData(filteredData.data);
          }
          if (!isComparedToEnabled || isComparisonQuery) {
            unstable_batchedUpdates(() => {
              setAreRowsUpdating(false);
              setIsChartUpdating(false);
            });
          }
        });
      })
      .catch((error) => {
        unstable_batchedUpdates(() => {
          setDataLoadState(LoadingStateNoData);
          setAreRowsUpdating(false);
          setIsChartUpdating(false);
          setSnackbar({
            message: error.message,
            error: true,
            warning: false,
          });
        });
      });
  };

  return (
    <ReportsDashboardContext.Provider value={{ cfg }}>
      <Panels maxWidth vertical relative minWidthMain>
        <Panel
          largeHorizontalPadding
          verticalPadding
          style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
        >
          <ModelTopBar
            title={routeName(pathname)}
            session={session}
            user={user}
            publisher={session.publisher}
            classes={classes}
            currentPath={pathname}
            disableTitlePaddingLeft
          />
        </Panel>
        <Panel scroll paddingTop={spacing.small}>
          <Panels maxWidth vertical relative largeHorizontalPadding>
            <Panel paper marginTop={spacing.base}>
              <ReportsDashboardFilters
                user={user}
                session={session}
                // timezone props
                timezone={timezone}
                timezonesList={timezonesList}
                handleTimezoneOnChange={handleTimezoneOnChange}
                // calendar props
                range1Selected={rangeSelectedStr}
                range1DisplaySelected={rangeSelectedStr}
                calStartDate={calendar.startDate}
                calEndDate={calendar.endDate}
                calDisplayStartDate={calendar.displayStartDate}
                calDisplayEndDate={calendar.displayEndDate}
                calFocusedInput={calFocusedInput}
                // compare calendar props
                range2Selected={compareCalendar.rangeSelected}
                range2DisplaySelected={compareCalendar.rangeDisplaySelected}
                compStartDate={compareCalendar.startDate}
                compEndDate={compareCalendar.endDate}
                compDisplayStartDate={compareCalendar.displayStartDate}
                compDisplayEndDate={compareCalendar.displayEndDate}
                compFocusedInput={compFocusedInput}
                // calendar functions
                handleChangeAggRangeSelected={handleChangeAggRangeSelected}
                handleCalendarDateChange={handleCalendarDateChange}
                handleDateRangeChange={handleDateRangeChange}
                handleCalendarFocusedInputChange={handleCalendarFocusedInputChange}
                // comparison toggle
                isComparedToEnabled={isComparedToEnabled}
                handleCompareEnabled={handleCompareEnabled}
                // filters
                filters={Object.values(filters)}
                handleClearFilters={handleClearFilters}
                isClearFiltersEnabled={isFiltersSelected(filters)}
                // filter pill functions
                handleFilterOnChange={handleFilterOnChange}
                handleFilterAccept={handleFilterAccept}
                // view by select
                currentGroupBy={currentGroupBy}
                groupBySelectItems={generateGroupBySelectItemsFromConfig(cfg.groupBys)}
                handleGroupByOnChange={handleGroupByOnChange}
                // TODO: change order by to metrics and threshold
                currentOrderBy={currentOrderBy}
                orderBySelectItems={generateOrderBySelectItemsFromConfig(cfg.mainMetrics)}
                handleOrderByOnChange={handleOrderByOnChange}
                orderByList={currentOrderByList}
                handleOrderByListOnChange={handleOrderByListOnChange}
                currentThreshold={currentThreshold}
                thresholdSelectedItems={generateThresholdSelectItemsFromConfig(cfg.threshold)}
                handleThresholdOnChange={handleThresholdOnChange}
              />
            </Panel>
            {/* MAIN SECTION */}
            <Panel>
              <DashboardBreadCrumbs
                initialMessage=''
                items={generateBreadCrumbItems(drillItems)}
                onClick={handleBreadCrumbClick}
              />
            </Panel>
            <Panel
              paper
              style={{
                margin: '16px 0px',
                paddingBottom: spacing.small,
              }}
            >
              <Rows>
                {/* CHART */}
                <Row noPadding>
                  <DrillHorizontalBarChart
                    dataLoadState={dataLoadState}
                    data={displayedChartData}
                    eCurrencyType={CurrencyType}
                    isComparedToEnabled={isComparedToEnabled}
                    title={`Top ${drillGroupBy.pluralLabel} by ${currentOrderBy.label}`}
                    isChartUpdating={isChartUpdating}
                    handleBarClick={handleRowClick}
                    selectedDateRange={rangeSelectedStr}
                  />
                </Row>
                {/* TABLE */}
                <Row style={{ paddingBottom: spacing.small }}>
                  <DrillTable
                    cols={cols}
                    rows={rows}
                    metricKeys={metricKeys}
                    metricValueTypes={cfg.mainMetrics}
                    eCurrencyType={CurrencyType}
                    orderBy={currentOrderBy}
                    orderDirection={currentOrderBy.defaultOrderDirection}
                    totalRows={
                      !isEmpty(data)
                        ? limitRowCount(Object.values(data)[0].TotalRows as number, 100)
                        : 0
                    }
                    rowsPerPageOptions={cfg.table.rowsPerPageOptions}
                    rowsPerPage={cfg.table.defaultRowsPerPage}
                    areRowsUpdating={areRowsUpdating}
                    handleRowClick={handleRowClick}
                    areRowsClickable={drillItems.length < currentGroupBy.drillPath.length}
                    handleTableDisplayChange={handleTableDisplayChange}
                    areRowsAllData
                  />
                </Row>
              </Rows>
            </Panel>
            <PanelBreak />
          </Panels>
        </Panel>
        <SnackbarView
          autoHideDuration={12000}
          open={!!snackbar.message}
          error={!!snackbar.error}
          onClose={handleSnackbarClose}
          messageColor={''}
          backgroundColor={''}
          closeColor={''}
          warning={!!snackbar.warning}
        >
          {snackbar.message}
        </SnackbarView>
      </Panels>
    </ReportsDashboardContext.Provider>
  );
};
